import React, { useEffect, useState, useMemo } from "react";
import {
  Package,
  AlertTriangle,
  Play,
  Square,
  Loader,
  Thermometer,
  ShieldAlert,
  Upload,
  File,
  Eye,
  Download,
  Trash2,
  Paperclip,
  Phone,
  Mail,
  Plus,
  ShieldCheck,
  Home,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  fetchShipment,
  setAwaitingPickup,
  startJourney,
  completeJourney,
  clearDriverState,
  clearError,
} from "../../store/slices/driverSlice";
import { getCountries } from "../../store/slices/companySlice";
import { Country } from "../../types";
import { toast } from "react-toastify";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { DriverAttachment } from "../../types";
import { Reclamation } from "../../types/reclamation";
import {
  uploadDriverAttachments,
  downloadDriverAttachment,
  deleteDriverAttachment,
  addService,
} from "../../store/slices/driverSlice";
import { createCmrPdf } from "../../utils/pdfGenerator";
import ConfirmationDialog from "../../components/modals/ConfirmationDialog";
import { CommentSection } from "../../components/comment/CommentSection";
import { getCurrentCoordinates } from "../../utils/geoHelper";
import {
  publicReclamationAPI,
  driverPublicAPI,
  driversAPI,
} from "../../services/api";
import { SignedDocumentsSection } from "../../components/shipments/SignedDocumentsSection";
import type { SignedDocument } from "../../types";
import DriverReclamationModal from "../../components/shipments/DriverReclamationModal";
import { STATUS_STEPS } from "../../constants/formOptions";
import { displayCargoNumber } from "../../utils/cargoDisplay";
import { useAuth } from "../../hooks/useAuth";
import { getServices } from "../../store/slices/servicesSlice";
import { AddServiceModal } from "../../components/modals/AddServiceFromDrivers";
import { createDeliveryNotePdf } from "../../utils/generateDeliveryNote";
import { isShipmentDocumentReady } from "../../utils/shipmentDocumentStatus";
import { vehicleRefLabel } from "../../utils/vehicleLabel";

const DriverView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("shipments");
  const { user } = useAuth();
  const { shipment, loading, error, attachmentLoading, attachmentError } =
    useAppSelector((state) => state.driver);
  const { services } = useAppSelector((state) => state.services);
  const [actionLoading, setActionLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const attachments = shipment?.driverAttachments || [];
  const pohAttachments = attachments.filter(
    (att) => att.attachmentType === "poh",
  );
  const regularDriverAttachments = attachments.filter(
    (att) => att.attachmentType !== "poh",
  );
  const totalAttachmentCount =
    (shipment?.driverAttachments?.length ?? 0) +
    (shipment?.publicAttachments?.length ?? 0) +
    (shipment?.attachments?.length ?? 0);
  const [activeTab, setActiveTab] = useState<"attachments" | "services">(
    "services",
  );
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    type: "warning" | "danger";
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: "Confirm",
    type: "warning",
  });

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");

  const [pohUploadModalOpen, setPohUploadModalOpen] = useState(false);
  const [pohUploadStep, setPohUploadStep] = useState<1 | 2>(1);

  const [pickupUploadModalOpen, setPickupUploadModalOpen] = useState(false);
  const [pickupStagedFiles, setPickupStagedFiles] = useState<File[]>([]);
  const [completeStagedFiles, setCompleteStagedFiles] = useState<File[]>([]);

  const [reclamations, setReclamations] = useState<Reclamation[]>([]);
  const [reclamationsLoading, setReclamationsLoading] = useState(false);
  const [showReclamationModal, setShowReclamationModal] = useState(false);

  const [searchParams] = useSearchParams();
  const params = useParams<{ id?: string; token?: string }>();
  const id = params.id ?? "";
  const tokenFromQuery = searchParams.get("token") ?? "";
  const tokenFromPath = params.token ?? "";
  const token = tokenFromQuery || tokenFromPath;

  const isPublicAccess = !!token;
  const isAuthenticatedAccess = !!id && !isPublicAccess;
  const isInternational = shipment?.type === "International";

  // Signed Documents (permanent/immutable). Public link uses the token-based
  // API + token id; authenticated driver uses the bearer API + shipment id.
  const signedApi = isPublicAccess ? driverPublicAPI : driversAPI;
  const signedRef = isPublicAccess ? token : id;
  const [signedDocs, setSignedDocs] = useState<SignedDocument[]>(
    shipment?.signedDocuments ?? [],
  );
  useEffect(() => {
    setSignedDocs(shipment?.signedDocuments ?? []);
  }, [shipment?._id, shipment?.signedDocuments]);

  const otpRequired = !shipment?.deliveryOtp?.used;

  useEffect(() => {
    const initialize = async () => {
      try {
        dispatch(getCountries())
          .unwrap()
          .then((response) => setCountries(response))
          .catch((err) => {
            console.warn("[DriverView] Failed to load countries:", err);
            setCountries([]);
          });

        if (isPublicAccess) {
          await dispatch(fetchShipment({ token }));
        } else if (isAuthenticatedAccess && user?.role === "driver") {
          await dispatch(fetchShipment({ token: id, isProtected: true }));
        }

        await dispatch(
          getServices({
            search: "",
            page: 1,
            limit: 1000,
            partnerId: "GENERAL",
            typeNot: "MATRIX",
          }),
        );
      } finally {
        setIsInitialized(true);
      }
    };
    initialize();
    return () => {
      dispatch(clearDriverState());
    };
  }, [dispatch, token, id, isPublicAccess, isAuthenticatedAccess, user?.role]);

  const loadReclamations = async (shipmentId: string) => {
    try {
      setReclamationsLoading(true);
      const res = await publicReclamationAPI.getReclamations(
        shipmentId,
        "driver",
      );
      setReclamations(res.data.data?.reclamations ?? []);
    } catch {
      // silently fail
    } finally {
      setReclamationsLoading(false);
    }
  };

  const shipmentId = shipment?._id || id;

  useEffect(() => {
    if (shipmentId) {
      loadReclamations(shipmentId);
    }
  }, [shipmentId]);

  const countryOptions = useMemo(
    () =>
      countries.flat().map((country) => ({
        value: country._id,
        label: country.name,
      })),
    [countries],
  );

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
    if (attachmentError) {
      toast.error(attachmentError);
      dispatch(clearError());
    }
  }, [error, dispatch, attachmentError]);

  const executeCompleteJourney = async (otp?: string) => {
    if (!shipment) return;
    setActionLoading(true);
    try {
      const coords = await getCurrentCoordinates();
      const coordsPayload = coords ?? { latitude: 0, longitude: 0 };

      if (isPublicAccess) {
        await dispatch(
          completeJourney({ token, ...coordsPayload, otp }),
        ).unwrap();
      } else if (isAuthenticatedAccess && user?.role === "driver") {
        await dispatch(
          completeJourney({
            token: id,
            ...coordsPayload,
            isProtected: true,
            otp,
          }),
        ).unwrap();
      }

      toast.success(t("driverView.journeyComplete"));
      setOtpModalOpen(false);
      setOtpInput("");
      setOtpError("");
    } catch (error: unknown) {
      const msg =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Failed to complete journey";

      if (msg.toLowerCase().includes("otp")) {
        setOtpError(msg);
      } else {
        toast.error(msg);
        setOtpModalOpen(false);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const executeStartJourney = async () => {
    if (!shipment) return;
    setActionLoading(true);
    try {
      const coords = await getCurrentCoordinates();
      const coordsPayload = coords ?? { latitude: 0, longitude: 0 };

      if (shipment.status === "ReadyForPickup" || shipment.status === "Order") {
        if (isPublicAccess) {
          await dispatch(
            setAwaitingPickup({ token, ...coordsPayload }),
          ).unwrap();
        } else if (isAuthenticatedAccess && user?.role === "driver") {
          await dispatch(
            setAwaitingPickup({
              token: id,
              ...coordsPayload,
              isProtected: true,
            }),
          ).unwrap();
        }
        toast.success(t("driverView.shipmentSetPickedUp"));
      } else if (shipment.status === "AwaitingPickup") {
        if (isPublicAccess) {
          await dispatch(startJourney({ token, ...coordsPayload })).unwrap();
        } else if (isAuthenticatedAccess && user?.role === "driver") {
          await dispatch(
            startJourney({ token: id, ...coordsPayload, isProtected: true }),
          ).unwrap();
        }
        toast.success(t("driverView.journeyStarted"));
      }
    } catch {
      // intentionally silent
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartJourney = () => {
    if (!shipment) return;

    let title, message, confirmText;

    if (
      shipment.pohLevel === 0 &&
      (shipment.status === "ReadyForPickup" || shipment.status === "Order")
    ) {
      setPickupStagedFiles([]);
      setPickupUploadModalOpen(true);
      return;
    }

    if (shipment.status === "ReadyForPickup" || shipment.status === "Order") {
      title = t("driverView.confirmPickupTitle");
      message = t("driverView.confirmPickupMessage");
      confirmText = t("driverView.confirmPickupText");
    } else if (shipment.status === "AwaitingPickup") {
      title = t("driverView.confirmStartTitle");
      message = t("driverView.confirmStartMessage");
      confirmText = t("driverView.confirmStartText");
    }

    setConfirmDialog({
      isOpen: true,
      title: title || t("driverView.confirmReadyTitle"),
      message: message || "",
      onConfirm: executeStartJourney,
      confirmText: confirmText || t("driverView.confirmReadyText"),
      type: "warning",
    });
  };

  const handleCompleteJourney = () => {
    if (!shipment) return;

    if (shipment.pohLevel === 0) {
      setPohUploadStep(1);
      setCompleteStagedFiles([]);
      setPohUploadModalOpen(true);
      return;
    }

    if (otpRequired) {
      setOtpInput("");
      setOtpError("");
      setOtpModalOpen(true);
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: t("driverView.confirmCompleteTitle"),
      message: t("driverView.confirmCompleteMessage"),
      onConfirm: executeCompleteJourney,
      confirmText: t("driverView.confirmCompleteText"),
      type: "danger",
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Order":
        return "bg-gray-100 text-gray-800";
      case "ReadyForPickup":
        return "bg-purple-100 text-purple-700";
      case "AwaitingPickup":
        return "bg-orange-100 text-orange-800";
      case "InTransit":
        return "bg-blue-100 text-blue-800";
      case "Completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getButtonText = () => {
    if (!shipment) return t("driverView.startJourney");
    switch (shipment.status) {
      case "Order":
        return t("driverView.setPickedUp");
      case "ReadyForPickup":
        return t("driverView.setPickedUp");
      case "AwaitingPickup":
        return t("driverView.startJourney");
      case "InTransit":
        return t("driverView.journeyInProgress");
      case "Completed":
        return t("driverView.journeyCompleted");
      default:
        return t("driverView.startJourney");
    }
  };

  const canStartJourney =
    shipment &&
    (shipment.status === "Order" ||
      shipment.status === "ReadyForPickup" ||
      shipment.status === "AwaitingPickup");
  const canCompleteJourney = shipment && shipment.status === "InTransit";

  const formatTimestamp = (timestamp: string | number | Date) => {
    const date =
      timestamp instanceof Date
        ? timestamp
        : new Date(
            typeof timestamp === "number"
              ? timestamp
              : Number(timestamp) || timestamp,
          );
    return date.toLocaleString();
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    asPoh = false,
    onSuccess?: () => void,
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const formData = new FormData();
    files.forEach((file) => formData.append("attachments", file));
    if (asPoh) formData.append("attachmentType", "poh");
    try {
      if (isPublicAccess) {
        await dispatch(uploadDriverAttachments({ token, formData })).unwrap();
      } else if (isAuthenticatedAccess && user?.role === "driver") {
        await dispatch(
          uploadDriverAttachments({ token: id, formData, isProtected: true }),
        ).unwrap();
      }
      onSuccess?.();
      toast.success(t("driverView.uploadSuccess"));
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Upload failed";
      if (!navigator.onLine) {
        toast.info(message);
      } else {
        toast.error(message);
      }
    } finally {
      e.target.value = "";
    }
  };

  const stageFiles = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeStagedFile = (
    index: number,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadStagedFilesAsPoh = async (files: File[]) => {
    if (files.length === 0) return;
    const formData = new FormData();
    files.forEach((file) => formData.append("attachments", file));
    formData.append("attachmentType", "poh");
    if (isPublicAccess) {
      await dispatch(uploadDriverAttachments({ token, formData })).unwrap();
    } else if (isAuthenticatedAccess && user?.role === "driver") {
      await dispatch(
        uploadDriverAttachments({ token: id, formData, isProtected: true }),
      ).unwrap();
    }
  };

  const confirmPickupWithUpload = async () => {
    setActionLoading(true);
    try {
      await uploadStagedFilesAsPoh(pickupStagedFiles);
      setPickupStagedFiles([]);
      await executeStartJourney();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Upload failed";
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmCompleteJourneyWithUpload = async () => {
    setActionLoading(true);
    try {
      await uploadStagedFilesAsPoh(completeStagedFiles);
      setCompleteStagedFiles([]);
      setPohUploadModalOpen(false);
      await executeCompleteJourney();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Upload failed";
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const removeAttachment = async (aid: string) => {
    try {
      if (isPublicAccess) {
        await dispatch(
          deleteDriverAttachment({ token, attachmentId: aid }),
        ).unwrap();
      } else if (isAuthenticatedAccess && user?.role === "driver") {
        await dispatch(
          deleteDriverAttachment({
            token: id,
            attachmentId: aid,
            isProtected: true,
          }),
        ).unwrap();
      }
      toast.success(t("driverView.deleteSuccess"));
    } catch {
      // intentionally silent
    }
  };

  const downloadAttachment = async (attachment: DriverAttachment) => {
    try {
      if (isPublicAccess) {
        await dispatch(
          downloadDriverAttachment({
            token,
            attachmentId: attachment._id,
            originalName: attachment.originalName,
          }),
        ).unwrap();
      } else if (isAuthenticatedAccess && user?.role === "driver") {
        await dispatch(
          downloadDriverAttachment({
            token: id,
            attachmentId: attachment._id,
            originalName: attachment.originalName,
            isProtected: true,
          }),
        ).unwrap();
      }
    } catch {
      // intentionally silent
    }
  };

  const openAttachment = (attachment: DriverAttachment) => {
    window.open(attachment.fileUrl, "_blank");
  };

  const downloadStaticFile = async (file: {
    fileUrl: string;
    originalName: string;
  }) => {
    try {
      const response = await fetch(file.fileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // intentionally silent
    }
  };

  const handleOpenFile = async () => {
    try {
      if (!shipment) return;
      let pdfBytes: Uint8Array;
      if (isInternational) {
        pdfBytes = await createCmrPdf(shipment, countries);
      } else {
        pdfBytes = await createDeliveryNotePdf(shipment);
      }
      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : t("driverView.openFileFailed"),
      );
    }
  };

  const handleDownloadFile = async () => {
    try {
      if (!shipment) return false;
      let pdfBytes: Uint8Array;
      if (isInternational) {
        pdfBytes = await createCmrPdf(shipment, countries);
      } else {
        pdfBytes = await createDeliveryNotePdf(shipment);
      }
      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const filePrefix = isInternational ? "CMR" : "Delivery_Note";
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filePrefix}_${shipment.shipmentNumber}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("driverView.downloadFileFailed"),
      );
      return false;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const handleAddService = () => setShowAddServiceModal(true);

  const openInMaps = (
    street: string,
    index: string,
    city: string,
    country:
      | string
      | { _id: string; name: string; code: string; region: string },
  ) => {
    const foundCountry =
      countryOptions.find((option) => option.value === country)?.label || "";
    const address = [street, `${index} ${city}`, foundCountry]
      .filter(Boolean)
      .join(", ");
    window.open(
      `https://maps.google.com/maps?q=${encodeURIComponent(address)}`,
      "_blank",
    );
  };

  // ── Loading / error states ──────────────────────────────────────────────────

  if (!isInitialized || (loading && !shipment)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isAuthenticatedAccess && user?.role !== "driver") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 text-center">
        <div>
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">
            {t("driverView.accessDenied")}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {t("driverView.accessDeniedDesc")}
          </p>
        </div>
      </div>
    );
  }

  if (isInitialized && !shipment && !loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 text-center">
        <div>
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">
            {t("driverView.noShipmentFound")}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {t("driverView.noShipmentDesc", {
              method: isPublicAccess
                ? t("driverView.token")
                : t("driverView.shipmentId"),
            })}
          </p>
        </div>
      </div>
    );
  }

  // ── Main mobile view ────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-[600px] mx-auto pb-20">
        {/* ── 1. Header ── */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white mb-1">
          {isAuthenticatedAccess && (
            <button
              onClick={() => navigate("/driver-home")}
              className="shrink-0 p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={t("driver.home")}
              title={t("driver.home")}
            >
              <Home className="h-5 w-5 text-gray-600" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900">
                {shipment?.shipmentNumber}
              </span>
              {shipment?.referenceNumber && (
                <span className="text-sm text-gray-400">
                  ({shipment.referenceNumber})
                </span>
              )}
            </div>
            {shipment?.client?.companyName && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {shipment.client.companyName}
              </p>
            )}
          </div>
          <span
            className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusColor(shipment?.status || "")}`}
          >
            {STATUS_STEPS.find((s) => s.key === shipment?.status)?.label ||
              shipment?.status}
          </span>
        </div>

        {/* ── 2. OTP Banner ── */}
        {shipment?.deliveryOtp?.required && (
          <div
            className={`flex items-center gap-2 px-4 py-2.5 text-xs mb-1 ${
              shipment.deliveryOtp.used
                ? "bg-green-50 text-green-800 border-green-100"
                : "bg-amber-50 text-amber-800 border-amber-100"
            }`}
          >
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
            <span>
              {shipment.deliveryOtp.used
                ? "OTP verified — delivery confirmed."
                : "Receiver OTP required to complete this journey."}
            </span>
          </div>
        )}

        {/* ── 3. Route Timeline ── */}
        <div className="px-4 py-3 bg-white mb-1">
          {/* Pickup */}
          <div className="flex gap-3 mb-0">
            <div className="flex flex-col items-center pt-1 shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
              <div
                className="w-px bg-gray-200 flex-1 my-1"
                style={{ minHeight: 22 }}
              />
            </div>
            <div className="flex-1 min-w-0 pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-tight truncate">
                    {shipment?.pickup?.companyName || "-"}
                  </p>
                  {shipment?.pickup?.address && (
                    <button
                      onClick={() =>
                        openInMaps(
                          shipment.pickup.address?.street?.split(",")[0] || "",
                          shipment.pickup.address?.postalCode || "",
                          shipment.pickup.address?.city || "",
                          shipment.pickup.address?.country || "",
                        )
                      }
                      className="text-xs text-blue-600 text-left hover:underline mt-0.5 block"
                    >
                      {[
                        shipment.pickup.address.street?.split(",")[0],
                        shipment.pickup.address.postalCode,
                        shipment.pickup.address.city,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </button>
                  )}
                </div>
                {shipment?.pickup?.date && (
                  <span className="text-xs font-medium text-gray-600 shrink-0">
                    {new Date(
                      shipment.pickup.date.split("T")[0],
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  </span>
                )}
              </div>
              {(shipment?.pickup?.phoneNumber ||
                shipment?.pickup?.emailAddress) && (
                <div className="flex items-center gap-4 mt-1.5">
                  {shipment.pickup.phoneNumber && (
                    <a
                      href={`tel:${shipment.pickup.phoneNumber}`}
                      className="flex items-center gap-1 text-xs text-blue-600 active:opacity-60"
                    >
                      <Phone className="h-3 w-3" />
                      <span>{shipment.pickup.phoneNumber}</span>
                    </a>
                  )}
                  {shipment.pickup.emailAddress && (
                    <a
                      href={`mailto:${shipment.pickup.emailAddress}?subject=${encodeURIComponent(t("driverView.pickupInquiry", { number: shipment.shipmentNumber ?? "" }))}`}
                      className="flex items-center gap-1 text-xs text-green-700 active:opacity-60"
                    >
                      <Mail className="h-3 w-3" />
                      <span className="truncate max-w-[140px]">
                        {shipment.pickup.emailAddress}
                      </span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Delivery */}
          <div className="flex gap-3">
            <div className="pt-1 shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-tight truncate">
                    {shipment?.delivery?.companyName || "-"}
                  </p>
                  {shipment?.delivery?.address && (
                    <button
                      onClick={() =>
                        openInMaps(
                          shipment.delivery.address?.street?.split(",")[0] ||
                            "",
                          shipment.delivery.address?.postalCode || "",
                          shipment.delivery.address?.city || "",
                          shipment.delivery.address?.country || "",
                        )
                      }
                      className="text-xs text-blue-600 text-left hover:underline mt-0.5 block"
                    >
                      {[
                        shipment.delivery.address.street?.split(",")[0],
                        shipment.delivery.address.postalCode,
                        shipment.delivery.address.city,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </button>
                  )}
                </div>
                {shipment?.delivery?.date && (
                  <span className="text-xs font-medium text-gray-600 shrink-0">
                    {new Date(
                      shipment.delivery.date.split("T")[0],
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  </span>
                )}
              </div>
              {(shipment?.delivery?.phoneNumber ||
                shipment?.delivery?.emailAddress) && (
                <div className="flex items-center gap-4 mt-1.5">
                  {shipment.delivery.phoneNumber && (
                    <a
                      href={`tel:${shipment.delivery.phoneNumber}`}
                      className="flex items-center gap-1 text-xs text-blue-600 active:opacity-60"
                    >
                      <Phone className="h-3 w-3" />
                      <span>{shipment.delivery.phoneNumber}</span>
                    </a>
                  )}
                  {shipment.delivery.emailAddress && (
                    <a
                      href={`mailto:${shipment.delivery.emailAddress}?subject=${encodeURIComponent(t("driverView.deliveryInquiry", { number: shipment.shipmentNumber ?? "" }))}`}
                      className="flex items-center gap-1 text-xs text-green-700 active:opacity-60"
                    >
                      <Mail className="h-3 w-3" />
                      <span className="truncate max-w-[140px]">
                        {shipment.delivery.emailAddress}
                      </span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── 4. Transport Strip ── */}
        <div className="grid grid-cols-2 divide-x divide-gray-200 bg-white mb-1">
          <div className="px-3 py-2 text-center">
            <p className="text-xs text-gray-400">{t("driverView.truck")}</p>
            <p className="text-xs font-semibold text-gray-800 truncate mt-0.5">
              {vehicleRefLabel(shipment?.carrier?.truckLicensePlate) || "-"}
            </p>
          </div>
          <div className="px-3 py-2 text-center">
            <p className="text-xs text-gray-400">{t("driverView.trailer")}</p>
            <p className="text-xs font-semibold text-gray-800 truncate mt-0.5">
              {vehicleRefLabel(shipment?.carrier?.trailerLicensePlate) || "-"}
            </p>
          </div>
        </div>

        {/* ── 5. Cargo Details ── */}
        <div className="bg-white mb-1">
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {t("driverView.cargoInfo")}
            </span>
            <span className="text-xs text-gray-500">
              {shipment?.totalQuantity || 0} {t("driverView.qty")} ·{" "}
              {t("driverView.weightKg", {
                value: shipment?.totalWeight?.toFixed(1) || 0,
              })}
            </span>
          </div>
          {shipment?.cargoItems && shipment.cargoItems.length > 0 ? (
            <div className="overflow-x-auto px-4 pb-3">
              <table className="w-full min-w-[300px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left pb-1.5 pr-4 text-xs font-normal text-gray-400">
                      {t("driverView.qty")}
                    </th>
                    <th className="text-left pb-1.5 pr-4 text-xs font-normal text-gray-400">
                      {t("driverView.type")}
                    </th>
                    <th className="text-left pb-1.5 pr-4 text-xs font-normal text-gray-400">
                      {t("driverView.weight")}
                    </th>
                    <th className="text-left pb-1.5 pr-4 text-xs font-normal text-gray-400">
                      {t("driverView.dimensions")}
                    </th>
                    <th className="text-left pb-1.5 text-xs font-normal text-gray-400">
                      {t("driverView.description")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shipment.cargoItems.map((item, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td className="py-1.5 pr-4 text-sm font-bold text-gray-900">
                        {displayCargoNumber(item.quantity)}
                      </td>
                      <td className="py-1.5 pr-4 text-sm text-gray-700">
                        {item.type || "-"}
                      </td>
                      <td className="py-1.5 pr-4 text-sm text-gray-700 whitespace-nowrap">
                        {displayCargoNumber(item.weightKg)} kg
                      </td>
                      <td className="py-1.5 pr-4 text-sm text-gray-700 whitespace-nowrap">
                        {displayCargoNumber(item.length)}×
                        {displayCargoNumber(item.width)}×
                        {displayCargoNumber(item.height)}
                      </td>
                      <td className="py-1.5 text-sm text-gray-600 max-w-[100px] truncate">
                        {item.description || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-gray-400 px-4 pb-3">
              {t("driverView.noCargoItems")}
            </p>
          )}
        </div>

        {/* ── 6. Cargo Handling ── */}
        {(shipment?.cargoHandling?.temperatureSensitive ||
          shipment?.cargoHandling?.adr ||
          shipment?.cargoHandling?.fragile) && (
          <div className="px-4 py-3 bg-white mb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              {t("driverView.cargoHandling")}
            </p>
            <div className="flex flex-wrap gap-2">
              {shipment.cargoHandling?.adr && (
                <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
                  <ShieldAlert className="h-4 w-4 text-orange-600" />
                  <span className="text-xs font-medium text-orange-700">
                    ADR
                    {shipment.cargoHandling.imoClass
                      ? ` · ${shipment.cargoHandling.imoClass}`
                      : ""}
                  </span>
                </div>
              )}
              {shipment.cargoHandling?.temperatureSensitive && (
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                  <Thermometer className="h-4 w-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">
                    {shipment.cargoHandling.temperatureRange ||
                      t("driverView.rangeNotSpecified")}
                  </span>
                </div>
              )}
              {shipment.cargoHandling?.fragile && (
                <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                  <Package className="h-4 w-4 text-red-600" />
                  <span className="text-xs font-medium text-red-700">
                    {t("driverView.handleWithCare")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── 7. Tabs ── */}
        <div className="flex border-b border-gray-200 sticky top-0 bg-white z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
          <button
            onClick={() => setActiveTab("services")}
            className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "services"
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-400"
            }`}
          >
            {t("driverView.services")}
            {(shipment?.services?.length ?? 0) > 0 && (
              <span className="ml-1 text-xs opacity-70">
                ({shipment!.services!.length})
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("attachments")}
            className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "attachments"
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-400"
            }`}
          >
            {t("driverView.attachments")}
            {totalAttachmentCount > 0 && (
              <span className="ml-1 text-xs opacity-70">
                ({totalAttachmentCount})
              </span>
            )}
          </button>
        </div>

        {/* ── 8. Tab Content ── */}
        {activeTab === "attachments" ? (
          <div className="bg-white mb-1">
            {/* Upload row */}
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("driverView.files")}
              </span>
              <div className="relative">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileUpload}
                  disabled={attachmentLoading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.zip,.rar"
                />
                <button
                  type="button"
                  disabled={attachmentLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium active:bg-green-700 disabled:opacity-50"
                >
                  {attachmentLoading ? (
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                  {t("driverView.files")}
                </button>
              </div>
            </div>

            {/* File rows */}
            <div className="divide-y divide-gray-50">
              {(shipment?.publicAttachments?.length ?? 0) > 0 && (
                <>
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    {t("publicView.clientLabel")}
                  </p>
                  {(shipment?.publicAttachments ?? []).map((att) => (
                    <div
                      key={att._id}
                      className="flex items-center gap-3 px-4 py-3 bg-blue-50/30"
                    >
                      <File className="h-4 w-4 text-blue-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 truncate">
                          {att.originalName}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(att.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => window.open(att.fileUrl, "_blank")}
                        className="p-2 text-gray-400 active:text-blue-600"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </>
              )}

              {(shipment?.attachments?.length ?? 0) > 0 && (
                <>
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    {t("readonly.adminLabel")}
                  </p>
                  {(shipment?.attachments ?? []).map((att) => (
                    <div
                      key={att._id}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      <File className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 truncate">
                          {att.originalName}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(att.size)}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={() => window.open(att.fileUrl, "_blank")}
                          className="p-2 text-gray-400 active:text-blue-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => downloadStaticFile(att)}
                          className="p-2 text-gray-400 active:text-green-600"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {pohAttachments.length > 0 && (
                <>
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    {t("readonly.pohLabel")}
                  </p>
                  {pohAttachments.map((att) => (
                    <div
                      key={att._id}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      <File className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 truncate">
                          {att.originalName}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(att.size)} ·{" "}
                          {formatTimestamp(att.uploadedAt)}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={() => openAttachment(att)}
                          className="p-2 text-gray-400 active:text-blue-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => downloadAttachment(att)}
                          className="p-2 text-gray-400 active:text-green-600"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {(regularDriverAttachments.length > 0 ||
                isShipmentDocumentReady(shipment)) && (
                <>
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    {t("readonly.driverLabel")}
                  </p>
                  {isShipmentDocumentReady(shipment) && (
                    <div className="flex items-center gap-3 px-4 py-3">
                      <File className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 truncate">
                          {isInternational
                            ? t("driverView.cmrDocument")
                            : t("driverView.deliveryNote")}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date().toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={handleOpenFile}
                          className="p-2 text-gray-400 active:text-blue-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleDownloadFile}
                          className="p-2 text-gray-400 active:text-green-600"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {regularDriverAttachments.map((att) => (
                    <div
                      key={att._id}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      <File className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 truncate">
                          {att.originalName}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatFileSize(att.size)} ·{" "}
                          {formatTimestamp(att.uploadedAt)}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={() => openAttachment(att)}
                          className="p-2 text-gray-400 active:text-blue-600"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => downloadAttachment(att)}
                          className="p-2 text-gray-400 active:text-green-600"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {!shipment?.driverAccess?.locked &&
                          shipment?.status !== "Completed" && (
                            <button
                              onClick={() => removeAttachment(att._id)}
                              className="p-2 text-gray-400 active:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {totalAttachmentCount === 0 &&
                !isShipmentDocumentReady(shipment) && (
                  <div className="flex items-center gap-2 px-4 py-3 text-gray-400">
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <p className="text-xs">{t("attachments.noAttachments")}</p>
                  </div>
                )}
            </div>

            {/* Signed Documents — permanent CMR/paperwork uploads (image/PDF),
                with preview + permanence confirmation. */}
            {signedRef && (
              <div className="px-3 pb-3">
                <SignedDocumentsSection
                  documents={signedDocs}
                  uploadFn={(formData) =>
                    signedApi.uploadSignedDocuments(signedRef, formData)
                  }
                  viewFn={(documentId, preview) =>
                    signedApi.downloadSignedDocument(
                      signedRef,
                      documentId,
                      preview,
                    )
                  }
                  onUploaded={(newDocs) =>
                    setSignedDocs((prev) => [...prev, ...newDocs])
                  }
                />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white mb-1">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("driverView.services")}
              </span>
              <button
                onClick={handleAddService}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium active:bg-green-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Edit
              </button>
            </div>
            {shipment?.services && shipment.services.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {shipment.services.map((service, idx) => {
                  const isDriver = service.addedBy === "driver";
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          isDriver
                            ? "bg-blue-600 text-white"
                            : "bg-green-600 text-white"
                        }`}
                      >
                        {isDriver
                          ? t("services.driverBadge")
                          : t("services.systemUser")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {service.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {service.description || service.type || "-"}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-gray-700 shrink-0">
                        ×{service.qty}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-3 text-gray-400">
                <Package className="h-3.5 w-3.5 shrink-0" />
                <p className="text-xs">{t("driverView.noServicesYet")}</p>
              </div>
            )}
          </div>
        )}

        {/* ── 9. Comments ── */}
        <div className="bg-white mb-1">
          <CommentSection
            token={token || id || ""}
            isAuth={isAuthenticatedAccess ? true : false}
          />
        </div>

        {/* ── 10. Reclamations ── */}
        <div className="bg-white">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Reclamations
                {reclamations.length > 0 ? ` (${reclamations.length})` : ""}
              </span>
            </div>
            {(isAuthenticatedAccess || isPublicAccess) && (
              <button
                onClick={() => setShowReclamationModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium rounded-lg active:bg-amber-100"
              >
                <Plus className="h-3 w-3" />
                {t("driverView.addReclamation")}
              </button>
            )}
          </div>

          {reclamationsLoading ? (
            <div className="flex items-center gap-2 px-4 pb-4 text-gray-400">
              <Loader className="h-4 w-4 animate-spin" />
              <span className="text-xs">Loading…</span>
            </div>
          ) : reclamations.length === 0 ? (
            <div className="flex flex-col items-center py-5 pb-6 text-gray-300">
              <AlertTriangle className="h-6 w-6 mb-1.5" />
              <p className="text-xs text-gray-400">No reclamations filed</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50 pb-2">
              {reclamations.map((rec: Reclamation) => {
                const statusColors: Record<string, string> = {
                  open: "bg-blue-100 text-blue-700",
                  under_review: "bg-amber-100 text-amber-700",
                  approved: "bg-green-100 text-green-700",
                  rejected: "bg-red-100 text-red-700",
                  settled: "bg-gray-100 text-gray-700",
                };
                const statusLabels: Record<string, string> = {
                  open: "Open",
                  under_review: "Under Review",
                  approved: "Approved",
                  rejected: "Rejected",
                  settled: "Settled",
                };
                const typeLabels: Record<string, string> = {
                  damage: "Damage",
                  loss: "Loss",
                  shortage: "Shortage",
                  delay: "Delay",
                  documentation: "Documentation",
                  other: "Other",
                };
                return (
                  <div key={rec._id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs font-semibold text-gray-700">
                          {rec.reclamationNumber}
                        </span>
                        <span className="text-xs text-gray-400">
                          {typeLabels[rec.type] ?? rec.type}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${statusColors[rec.status] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {statusLabels[rec.status] ?? rec.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{rec.description}</p>
                    {rec.claimedAmount != null && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {rec.currency} {rec.claimedAmount.toLocaleString()}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showReclamationModal && shipmentId && (
          <DriverReclamationModal
            tokenOrId={isPublicAccess ? token : id}
            shipmentNumber={shipment?.shipmentNumber ?? ""}
            isPublicAccess={isPublicAccess}
            onClose={() => setShowReclamationModal(false)}
            onSuccess={() => {
              setShowReclamationModal(false);
              loadReclamations(shipmentId);
            }}
          />
        )}

        {/* ── Sticky Bottom Action Bar ── */}
        {/* Level 2: the shipper's signature handles Order→ReadyForPickup and the
            consignee's signature handles InTransit→Completed, so the driver only
            acts on the middle transitions (ReadyForPickup, AwaitingPickup). */}
        {shipment?.status !== "Completed" &&
          !(
            shipment?.pohLevel === 1 && shipment?.status !== "AwaitingPickup"
          ) &&
          !(
            shipment?.pohLevel === 2 &&
            (shipment?.status === "Order" || shipment?.status === "InTransit")
          ) && (
            <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
              <div className="max-w-[600px] mx-auto px-4 py-3">
                <button
                  onClick={
                    canCompleteJourney
                      ? handleCompleteJourney
                      : handleStartJourney
                  }
                  disabled={
                    (!canStartJourney && !canCompleteJourney) ||
                    actionLoading ||
                    loading
                  }
                  className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    canCompleteJourney
                      ? "bg-green-600 active:bg-green-700"
                      : "bg-blue-600 active:bg-blue-700"
                  }`}
                >
                  {actionLoading || loading ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>{t("driverView.processing")}</span>
                    </>
                  ) : canCompleteJourney ? (
                    <>
                      <Square className="h-5 w-5" />
                      <span>{t("driverView.completeJourney")}</span>
                      {otpRequired && shipment?.pohLevel !== 0 && (
                        <ShieldCheck className="h-4 w-4 opacity-80" />
                      )}
                    </>
                  ) : (
                    <>
                      {shipment?.status === "ReadyForPickup" ||
                      shipment?.status === "Order" ? (
                        <Package className="h-5 w-5" />
                      ) : (
                        <Play className="h-5 w-5" />
                      )}
                      <span>{getButtonText()}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

        {/* ── 2-Step Upload Modal (pohLevel 0, no files) ── */}
        {pohUploadModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">
                  Complete Journey
                </h2>
                <div className="flex items-center gap-1.5">
                  <div
                    className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${pohUploadStep >= 1 ? "bg-green-600 text-white" : "bg-gray-200 text-gray-500"}`}
                  >
                    1
                  </div>
                  <div className="w-5 h-px bg-gray-300" />
                  <div
                    className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${pohUploadStep >= 2 ? "bg-green-600 text-white" : "bg-gray-200 text-gray-500"}`}
                  >
                    2
                  </div>
                </div>
              </div>

              {pohUploadStep === 1 ? (
                <>
                  <p className="text-sm text-gray-600">
                    Upload at least one document before completing the journey.
                  </p>
                  <div className="relative inline-flex w-full">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => stageFiles(e, setCompleteStagedFiles)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.zip,.rar"
                    />
                    <button
                      type="button"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      Choose files
                    </button>
                  </div>
                  {completeStagedFiles.length > 0 && (
                    <div className="space-y-1.5">
                      {completeStagedFiles.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-100"
                        >
                          <File className="h-4 w-4 text-green-600 shrink-0" />
                          <span className="text-xs text-gray-700 truncate flex-1">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              removeStagedFile(index, setCompleteStagedFiles)
                            }
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {completeStagedFiles.length === 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                      <span className="text-xs text-amber-700">
                        At least one new document is required to complete the
                        journey
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setCompleteStagedFiles([]);
                        setPohUploadModalOpen(false);
                      }}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm font-medium active:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setPohUploadStep(2)}
                      disabled={completeStagedFiles.length === 0}
                      className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-2">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Square className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-800">
                      Ready to complete the journey?
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {completeStagedFiles.length} file
                      {completeStagedFiles.length !== 1 ? "s" : ""} will be
                      uploaded. This cannot be undone.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPohUploadStep(1)}
                      disabled={actionLoading}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm font-medium active:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={confirmCompleteJourneyWithUpload}
                      disabled={actionLoading}
                      className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <Loader className="h-3.5 w-3.5 animate-spin" />
                          Processing…
                        </span>
                      ) : (
                        "Confirm"
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Pickup Upload Modal (pohLevel 0, ReadyForPickup / Order) ── */}
        {pickupUploadModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Picked Up
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Attach at least one document before confirming pickup
                  </p>
                </div>
                <Package className="h-5 w-5 text-orange-500 shrink-0" />
              </div>

              <div className="relative inline-flex w-full">
                <input
                  type="file"
                  multiple
                  onChange={(e) => stageFiles(e, setPickupStagedFiles)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.zip,.rar"
                />
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Choose files
                </button>
              </div>

              {pickupStagedFiles.length > 0 && (
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {pickupStagedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-lg border border-orange-100"
                    >
                      <File className="h-4 w-4 text-orange-500 shrink-0" />
                      <span className="text-xs text-gray-700 truncate flex-1">
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          removeStagedFile(index, setPickupStagedFiles)
                        }
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {pickupStagedFiles.length === 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="text-xs text-amber-700">
                    At least one document is required
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setPickupStagedFiles([]);
                    setPickupUploadModalOpen(false);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm font-medium active:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setPickupUploadModalOpen(false);
                    setConfirmDialog({
                      isOpen: true,
                      title: t("driverView.confirmPickupTitle"),
                      message: t("driverView.confirmPickupMessage"),
                      onConfirm: confirmPickupWithUpload,
                      confirmText: t("driverView.confirmPickupText"),
                      type: "warning",
                    });
                  }}
                  disabled={pickupStagedFiles.length === 0}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold active:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Pickup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── OTP Modal (pohLevel 1) ── */}
        {otpModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 shrink-0">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Enter delivery OTP
                  </h2>
                  <p className="text-xs text-gray-500">
                    Ask the receiver for their one-time code
                  </p>
                </div>
              </div>

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpInput}
                onChange={(e) => {
                  setOtpInput(e.target.value.replace(/\D/g, ""));
                  setOtpError("");
                }}
                placeholder="• • • • • •"
                className="w-full text-center text-3xl font-bold tracking-widest px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-300"
                autoFocus
              />

              {otpError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                  <p className="text-sm text-red-700">{otpError}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setOtpModalOpen(false);
                    setOtpInput("");
                    setOtpError("");
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm font-medium active:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => executeCompleteJourney(otpInput)}
                  disabled={otpInput.length !== 6 || actionLoading}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <Loader className="h-4 w-4 animate-spin" />
                      Verifying…
                    </span>
                  ) : (
                    "Confirm delivery"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <AddServiceModal
          show={showAddServiceModal}
          onClose={() => setShowAddServiceModal(false)}
          availableServices={services}
          existingServices={
            shipment?.services?.filter((s) => s.addedBy === "driver") || []
          }
          onSave={(updated) => {
            const shipmentToken = token || id;
            if (!shipmentToken) {
              toast.error(t("driverView.noServiceToken"));
              return;
            }
            if (isPublicAccess) {
              dispatch(addService({ token: shipmentToken, services: updated }));
            } else if (isAuthenticatedAccess && user?.role === "driver") {
              dispatch(
                addService({
                  token: shipmentToken,
                  services: updated,
                  isProtected: true,
                }),
              );
            }
          }}
        />

        <ConfirmationDialog
          isOpen={confirmDialog.isOpen}
          onClose={closeConfirmDialog}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          type={confirmDialog.type}
        />
      </div>
    </div>
  );
};

export default DriverView;
